import React, { useState, useMemo, useEffect } from "react";
import { tWithKeyAttr as t } from "../../i18nHelper";
import i18n from "../../i18n";
import DatePicker, { DateObject } from "react-multi-date-picker";
import { IPayment, ITransaction, IBalanceHistoryItem } from "../../services/types/utils.types";
import { useGetPaymentsByUserIdQuery, useGetUserBalanceHistoryQuery, useTransactionRefundMutation, useBulkTransactionsRefundMutation, useSendPaymentHistoryLinkMutation, useSendInvoiceBundleLinkMutation } from "../../services/api/utils.api";
import Loader from "../loader/loader";
import styles from "../paymentTable/paymentTable.module.css";
import { useAppSelector } from "../../services/hooks";
import { adminSelector } from "../../services/selectors/user.selectors";
import ExportTableButtons from "../exportTableButtons/ExportTableButtons";

interface UserPaymentTableProps {
  userId: number;
  userGuid: string;
  hideTitle?: boolean;
  clientPhone?: string;
  clientEmail?: string | null;
}

interface RefundModalState {
  isOpen: boolean;
  transactionId: number | null;
  transactionUid: string | null;
  transactionAmount: number;
  maxRefundAmount: number; // Maximum refundable amount (original - already refunded)
  refundAmount: string;
  refundReason: string;
  paymentsTypesId: number; // 1=Credit Card, 2=Bank Transfer, 3=Cash, 4=Check
}

const UserPaymentTable: React.FC<UserPaymentTableProps> = ({ userId, userGuid, hideTitle = false, clientPhone, clientEmail }) => {
  const admin = useAppSelector(adminSelector);
  const [refundError, setRefundError] = useState<string>("");
  const [refundSuccess, setRefundSuccess] = useState<string>("");
  const [refundingTransactionIds, setRefundingTransactionIds] = useState<Set<number>>(new Set());
  const [selectedTransactionIds, setSelectedTransactionIds] = useState<Set<number>>(new Set());
  const [isBulkRefunding, setIsBulkRefunding] = useState<boolean>(false);
  const [bulkRefundReason, setBulkRefundReason] = useState<string>("");
  const [showBulkRefundModal, setShowBulkRefundModal] = useState<boolean>(false);
  const [expandedText, setExpandedText] = useState<{ titleKey: string; content: string } | null>(null);
  const [refundModal, setRefundModal] = useState<RefundModalState>({
    isOpen: false,
    transactionId: null,
    transactionUid: null,
    transactionAmount: 0,
    maxRefundAmount: 0,
    refundAmount: "",
    refundReason: "",
    paymentsTypesId: 1 // Default to credit card
  });

  const { data: response, isLoading, error, refetch } = useGetPaymentsByUserIdQuery(
    { type: 1, companyId: admin.companyId, userId },
    { skip: !userId || userId <= 0 || !admin.companyId, refetchOnMountOrArgChange: true }
  );

  const [transactionRefund] = useTransactionRefundMutation();
  const [bulkTransactionsRefund] = useBulkTransactionsRefundMutation();
  const [sendPaymentHistoryLink, { isLoading: isSendingLink }] = useSendPaymentHistoryLinkMutation();
  const [sendInvoiceBundleLink, { isLoading: isSendingInvoices }] = useSendInvoiceBundleLinkMutation();

  const { data: balanceHistory } = useGetUserBalanceHistoryQuery(
    { userId },
    { skip: !userId || userId <= 0, refetchOnMountOrArgChange: true }
  );

  // Send Payment History state
  const [sendViaSms, setSendViaSms] = useState<boolean>(!!clientPhone);
  const [sendViaEmail, setSendViaEmail] = useState<boolean>(!!clientEmail);
  const [sendSuccess, setSendSuccess] = useState<string>("");
  const [sendError, setSendError] = useState<string>("");

  // Date range state
  type DatePreset = "all" | "3months" | "6months" | "1year" | "custom";
  const [datePreset, setDatePreset] = useState<DatePreset>("all");
  const [customFromDate, setCustomFromDate] = useState<DateObject | null>(null);
  const [customToDate, setCustomToDate] = useState<DateObject | null>(null);

  const isRtl = i18n.dir() === "rtl";

  // Auto-dismiss toast notifications after 5 seconds
  useEffect(() => {
    if (refundError) {
      const timer = setTimeout(() => setRefundError(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [refundError]);

  useEffect(() => {
    if (refundSuccess) {
      const timer = setTimeout(() => setRefundSuccess(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [refundSuccess]);

  useEffect(() => {
    if (sendSuccess) {
      const timer = setTimeout(() => setSendSuccess(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [sendSuccess]);

  useEffect(() => {
    if (sendError) {
      const timer = setTimeout(() => setSendError(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [sendError]);

  // Extract the payments array from the response
  const payments: IPayment[] = response?.data || [];

  // Flatten all transactions from standing orders and include standalone transactions
  // Standing orders (with paymentUid) contain child transactions in their transactions array
  // Standalone transactions (no paymentUid) are already individual records
  const allTransactions = payments.flatMap((payment) => {
    if (payment.paymentUid) {
      // This is a standing order - extract its child transactions
      return (payment.transactions || []).map((transaction) => ({
        ...transaction,
        // Inherit payment info from parent standing order if not present on transaction
        paymentName: transaction.paymentName || payment.paymentName,
        paymentsTypesId: transaction.paymentsTypesId ?? payment.paymentTypesId,
        paymentPeriodTypeName: transaction.paymentPeriodTypeName || payment.paymentPeriodTypeName,
        paymentClearingTypeName: transaction.paymentClearingTypeName || payment.paymentClearingTypeName,
        planName: transaction.planName || payment.planName,
        statusId: transaction.statusId ?? payment.statusId,
        paymentDocumentTypeName: transaction.paymentDocumentTypeName || payment.paymentDocumentTypeName,
        comments: transaction.comments || payment.comments,
        isRapidInvoiceRecover: transaction.isRapidInvoiceRecover ?? payment.isRapidInvoiceRecover,
      }));
    } else {
      // This is a standalone transaction - include it directly
      return [{
        id: payment.id,
        transactionUid: payment.transactionUid || '', // Use transactionUid from standalone payment
        transactionUidZikuy: payment.transactionUidZikuy, // Reference to original transaction if this is a refund
        parentId: 0,
        amount: payment.amount,
        timestamp: payment.timestamp,
        companyId: payment.companyId,
        paymentName: payment.paymentName,
        paymentsTypesId: payment.paymentTypesId,
        paymentsPeriodTypesId: payment.paymentsPeriodTypesId,
        paymentPeriodTypeName: payment.paymentPeriodTypeName,
        paymentsClearingTypesId: payment.paymentsClearingTypesId,
        paymentClearingTypeName: payment.paymentClearingTypeName,
        plansId: payment.plansId,
        planName: payment.planName,
        usersId: payment.usersId,
        invoiceNumber: payment.invoiceNumber,
        invoiceLink: payment.invoiceLink,
        statusId: payment.statusId,
        paymentDocumentTypeName: payment.paymentDocumentTypeName,
        comments: payment.comments,
        isRapidInvoiceRecover: payment.isRapidInvoiceRecover,
      }];
    }
  });

  // Type for flattened transactions
  type FlattenedTransaction = typeof allTransactions[number];

  // Calculate refund relationships and remaining amounts
  const refundData = useMemo(() => {
    // Map of transactionUid -> total refunded amount
    const refundedAmounts: Map<string, number> = new Map();
    // Map of transactionUid -> refund transactions that reference it
    const refundsForTransaction: Map<string, FlattenedTransaction[]> = new Map();
    // Set of transactionUids that are refunds themselves
    const isRefundTransaction: Set<string> = new Set();

    allTransactions.forEach(tx => {
      if (tx.transactionUidZikuy) {
        // This is a refund transaction
        isRefundTransaction.add(tx.transactionUid);

        // Add to the map of refunds for the original transaction
        const originalUid = tx.transactionUidZikuy;
        const existingRefunds = refundsForTransaction.get(originalUid) || [];
        existingRefunds.push(tx);
        refundsForTransaction.set(originalUid, existingRefunds);

        // Sum up refunded amounts (refunds have negative amounts, so use absolute value)
        const currentRefunded = refundedAmounts.get(originalUid) || 0;
        refundedAmounts.set(originalUid, currentRefunded + Math.abs(tx.amount));
      }
    });

    return { refundedAmounts, refundsForTransaction, isRefundTransaction };
  }, [allTransactions]);

  // Get remaining refundable amount for a transaction
  const getRemainingRefundAmount = (transactionUid: string, originalAmount: number): number => {
    const refundedAmount = refundData.refundedAmounts.get(transactionUid) || 0;
    return Math.max(0, originalAmount - refundedAmount);
  };

  // Check if a transaction is a refund
  const isRefund = (transactionUid: string): boolean => {
    return refundData.isRefundTransaction.has(transactionUid);
  };

  // Get the original transaction info for a refund
  const getOriginalTransactionForRefund = (transactionUidZikuy: string) => {
    return allTransactions.find(tx => tx.transactionUid === transactionUidZikuy);
  };

  // Check if a transaction has been refunded (partially or fully)
  const hasBeenRefunded = (transactionUid: string): boolean => {
    return refundData.refundsForTransaction.has(transactionUid);
  };

  // Get refunds for a transaction
  const getRefundsForTransaction = (transactionUid: string): FlattenedTransaction[] => {
    return refundData.refundsForTransaction.get(transactionUid) || [];
  };

  // Create hierarchical transaction list - group refunds under their parent transactions
  type HierarchicalTransaction = FlattenedTransaction & {
    isChildRefund?: boolean;
    isLastChild?: boolean;
    parentInvoice?: string;
    isManualAdjustment?: boolean;
    adminName?: string;
    prevBalance?: number;
    newBalance?: number;
  };

  const hierarchicalTransactions = useMemo((): HierarchicalTransaction[] => {
    
    const result: HierarchicalTransaction[] = [];
    const processedRefundIds = new Set<number>();
    
    // First, add all non-refund transactions, followed by their refunds
    allTransactions.forEach(tx => {
      // Skip if this is a refund (we'll add it under its parent)
      if (tx.transactionUidZikuy) return;
      
      // Add the parent transaction
      result.push({ ...tx, isChildRefund: false });
      
      // Add any refunds for this transaction immediately after
      const refunds = getRefundsForTransaction(tx.transactionUid);
      refunds.forEach((refund, idx) => {
        result.push({ 
          ...refund, 
          isChildRefund: true, 
          isLastChild: idx === refunds.length - 1,
          parentInvoice: tx.invoiceNumber
        });
        processedRefundIds.add(refund.id);
      });
    });
    
    // Add any orphaned refunds (refunds whose parent isn't in the current list)
    allTransactions.forEach(tx => {
      if (tx.transactionUidZikuy && !processedRefundIds.has(tx.id)) {
        result.push({ ...tx, isChildRefund: true, isLastChild: true });
      }
    });
    
    // Merge manual balance adjustment rows from UsersBalanceHistory
    if (balanceHistory && balanceHistory.length > 0) {
      balanceHistory.forEach(bh => {
        result.push({
          id: -bh.id, // negative to avoid collision with real transaction IDs
          transactionUid: `balance-${bh.id}`,
          parentId: 0,
          amount: bh.newBalance - bh.prevBalance,
          timestamp: bh.createdAt,
          companyId: 0,
          isChildRefund: false,
          isManualAdjustment: true,
          adminName: bh.adminName,
          prevBalance: bh.prevBalance,
          newBalance: bh.newBalance,
          comments: bh.updateReason,
          paymentName: '',
          paymentDocumentTypeName: '',
          invoiceNumber: '',
          invoiceLink: '',
        } as HierarchicalTransaction);
      });
    }

    // Sort all entries by timestamp descending so manual adjustments interleave correctly
    result.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    return result;
  }, [allTransactions, refundData, balanceHistory]);

  const formatDate = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return timestamp;
    }
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat("he-IL", {
      style: "currency",
      currency: "ILS",
    }).format(amount);
  };

  // Export rows: same columns as table, excluding Actions
  const exportRows = useMemo(() => {
    return hierarchicalTransactions.map((transaction) => {
      const txIsManualAdj = !!(transaction as HierarchicalTransaction).isManualAdjustment;
      const txIsRefund = !txIsManualAdj && !!transaction.transactionUidZikuy;
      const txHasBeenRefunded = !txIsManualAdj && hasBeenRefunded(transaction.transactionUid);
      const remainingAmount = !txIsManualAdj ? getRemainingRefundAmount(transaction.transactionUid, transaction.amount) : 0;
      const originalTx = txIsRefund ? getOriginalTransactionForRefund(transaction.transactionUidZikuy!) : null;

      let relationDisplay = "—";
      if (txIsManualAdj) {
        const adj = transaction as HierarchicalTransaction;
        relationDisplay = `${t("BALANCE_CHANGE")}: ⁦${formatAmount(adj.prevBalance ?? 0)} → ${formatAmount(adj.newBalance ?? 0)}⁩${adj.adminName ? ` (${adj.adminName})` : ''}`;
      } else if (txIsRefund) {
        relationDisplay = `↩️ #${transaction.parentInvoice || originalTx?.invoiceNumber || "-"}`;
      } else if (txHasBeenRefunded) {
        relationDisplay = `${t("TOTAL_PAID")}: ${formatAmount(remainingAmount)}`;
      }

      return {
        PaymentType: txIsManualAdj ? t("MANUAL_BALANCE_ADJUSTMENT") : (transaction.paymentName || t("CREDIT_CARD")),
        DocumentType: txIsManualAdj ? "-" : (transaction.paymentDocumentTypeName || "-"),
        PaymentAmount: txIsManualAdj
          ? `${transaction.amount >= 0 ? '+' : ''}${formatAmount(transaction.amount)}`
          : txIsRefund
          ? `-${formatAmount(Math.abs(transaction.amount))}`
          : formatAmount(transaction.amount),
        PaymentDate: formatDate(transaction.timestamp),
        InvoiceNumber: txIsManualAdj ? "-" : (transaction.invoiceNumber || "-"),
        RapidInvoiceRecover: txIsManualAdj
          ? "—"
          : transaction.isRapidInvoiceRecover
            ? t("RAPID_INVOICE_RECOVER_TAG")
            : "",
        Details: relationDisplay,
        Comments: transaction.comments || "—",
      };
    });
  }, [hierarchicalTransactions, refundData, balanceHistory, t]);

  const handleTransactionSelection = (transactionId: number, isSelected: boolean) => {
    setSelectedTransactionIds(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(transactionId);
      } else {
        newSet.delete(transactionId);
      }
      return newSet;
    });
  };

  const isTransactionSelected = (transactionId: number) => selectedTransactionIds.has(transactionId);

  const getStatusText = (statusId: number) => {
    switch (statusId) {
      case 1:
        return t('PAYMENT_PAID');
      case 2:
        return t('PAYMENT_PENDING');
      default:
        return `Status ${statusId}`;
    }
  };

  const getStatusClass = (statusId: number) => {
    switch (statusId) {
      case 1:
        return styles.statusPaid;
      case 2:
        return styles.statusPending;
      default:
        return styles.statusDefault;
    }
  };

  const rapidInvoiceRecoverMark = (isRapid?: boolean) =>
    isRapid ? (
      <span
        className={styles.rapidInvoiceRecoverMark}
        title={t("RAPID_INVOICE_RECOVER_TOOLTIP")}
        role="status"
        aria-label={t("RAPID_INVOICE_RECOVER_TOOLTIP")}
      >
        <span className={styles.rapidInvoiceRecoverMarkIcon} aria-hidden>
          ⚠
        </span>
        <span>{t("RAPID_INVOICE_RECOVER_TAG")}</span>
      </span>
    ) : null;

  const openRefundModal = (transactionId: number, transactionUid: string, transactionAmount: number, paymentsTypesId: number = 1) => {
    const maxRefund = getRemainingRefundAmount(transactionUid, transactionAmount);
    setRefundModal({
      isOpen: true,
      transactionId,
      transactionUid,
      transactionAmount,
      maxRefundAmount: maxRefund,
      refundAmount: maxRefund.toString(),
      refundReason: "",
      paymentsTypesId
    });
    setRefundError("");
    setRefundSuccess("");
  };

  const closeRefundModal = () => {
    setRefundModal({
      isOpen: false,
      transactionId: null,
      transactionUid: null,
      transactionAmount: 0,
      maxRefundAmount: 0,
      refundAmount: "",
      refundReason: "",
      paymentsTypesId: 1
    });
  };

  const handleRefundSubmit = async () => {
    if (!refundModal.transactionId || !refundModal.transactionUid) return;
    
    const refundAmount = parseFloat(refundModal.refundAmount);
    if (isNaN(refundAmount) || refundAmount <= 0) {
      setRefundError(t('INVALID_REFUND_AMOUNT'));
      return;
    }
    
    if (refundAmount > refundModal.maxRefundAmount) {
      setRefundError(t('REFUND_AMOUNT_EXCEEDS_REMAINING'));
      return;
    }
    
    if (!refundModal.refundReason.trim()) {
      setRefundError(t('REFUND_REASON_REQUIRED'));
      return;
    }

    const transactionId = refundModal.transactionId;
    
    // Add transaction to loading state
    setRefundingTransactionIds(prev => new Set(prev).add(transactionId));
    setRefundError("");
    setRefundSuccess("");

    try {
      // Build refund request based on payment type
      // Credit Card (1): Amount = original amount, PartialSum = refund amount (CardCom API requirement)
      // Other methods (2=Bank Transfer, 3=Cash, 4=Check): Amount = refund amount (only creates document)
      const isCreditCard = refundModal.paymentsTypesId === 1;
      
      const refundRequest = {
        UsersId: userId,
        TransactionId: refundModal.transactionUid,
        PaymentsTypesId: refundModal.paymentsTypesId,
        // For credit card: Amount is original transaction amount, PartialSum is the actual refund
        // For other methods: Amount is the refund amount
        Amount: isCreditCard ? refundModal.transactionAmount : refundAmount,
        // PartialSum only needed for credit card refunds
        PartialSum: isCreditCard ? refundAmount : undefined,
        Comments: refundModal.refundReason
      };
      
      // console.log('Refund request:', refundRequest);
      const result = await transactionRefund(refundRequest).unwrap();

      // Check if the transaction was already canceled/refunded in CardCom
      const alreadyCanceledMessages = [
        'לא ניתן לבטל עסקה שבוטלה', // Hebrew: Cannot cancel already canceled transaction
        'cannot cancel', // English fallback
        'already refunded',
        'already canceled'
      ];
      const isAlreadyCanceled = result.description && 
        alreadyCanceledMessages.some(msg => result.description?.toLowerCase().includes(msg.toLowerCase()));

      if (result.responseCode === 0) {
        setRefundSuccess(t('REFUND_SUCCESS', { amount: formatAmount(refundAmount), refundUid: result.newTranzactionId || '' }));
        closeRefundModal();
        // Refetch the data to update the table
        await refetch();
      } else if (isAlreadyCanceled) {
        // Transaction was already refunded in the payment gateway - refresh data to sync state
        setRefundError(t('TRANSACTION_ALREADY_REFUNDED'));
        closeRefundModal();
        await refetch();
      } else {
        setRefundError(t('ERROR_PROCESSING_REFUND'));
      }
    } catch (error: any) {
      // console.error('Error processing refund - full error:', JSON.stringify(error, null, 2));
      // console.error('Error data:', error?.data);
      // console.error('Error status:', error?.status);
      // Check various error response formats
      const errorMessage = error?.data?.Description || error?.data?.message || error?.data?.error || error?.message;
      if (errorMessage) {
        setRefundError(errorMessage);
      } else {
        setRefundError(t('ERROR_PROCESSING_REFUND'));
      }
    } finally {
      // Remove transaction from loading state
      setRefundingTransactionIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(transactionId);
        return newSet;
      });
    }
  };

  const handleBulkRefund = async () => {
    if (selectedTransactionIds.size === 0) {
      setRefundError(t('SELECT_TRANSACTIONS_FOR_REFUND'));
      return;
    }

    if (!bulkRefundReason.trim()) {
      setRefundError(t('REFUND_REASON_REQUIRED'));
      return;
    }

    setIsBulkRefunding(true);
    setRefundError("");
    setRefundSuccess("");

    try {
      const result = await bulkTransactionsRefund({
        transactionIds: Array.from(selectedTransactionIds),
        refundReason: bulkRefundReason
      }).unwrap();

      if (result.success) {
        const { summary } = result;
        setRefundSuccess(
          `${t('BULK_REFUND_COMPLETED')}! ${t('SUCCESSFUL_REFUNDS')}: ${summary.successfulRefunds}/${summary.totalRequested}, ${t('TOTAL_AMOUNT')}: ${formatAmount(summary.totalRefundAmount)}`
        );

        // Clear selections and close modal
        setSelectedTransactionIds(new Set());
        setShowBulkRefundModal(false);
        setBulkRefundReason("");

        // Refetch the data to update the table
        await refetch();
      } else {
        setRefundError(t('ERROR_PROCESSING_REFUND'));
      }
    } catch (error: any) {
      console.error('Error processing bulk refund:', error);
      if (error?.data?.message) {
        setRefundError(error.data.message);
      } else {
        setRefundError(t('ERROR_PROCESSING_REFUND'));
      }
    } finally {
      setIsBulkRefunding(false);
    }
  };

  const handleSendPaymentHistory = async () => {
    if (!sendViaSms && !sendViaEmail) return;

    setSendError("");
    setSendSuccess("");

    // Calculate delivery channel: 1=SMS, 2=Email, 3=Both
    const deliveryChannel = sendViaSms && sendViaEmail ? 3 : sendViaSms ? 1 : 2;

    // Calculate date range based on preset
    let fromDate: string | undefined;
    let toDate: string | undefined;
    const now = new Date();

    if (datePreset === "3months") {
      const from = new Date(now);
      from.setMonth(from.getMonth() - 3);
      fromDate = from.toISOString();
    } else if (datePreset === "6months") {
      const from = new Date(now);
      from.setMonth(from.getMonth() - 6);
      fromDate = from.toISOString();
    } else if (datePreset === "1year") {
      const from = new Date(now);
      from.setFullYear(from.getFullYear() - 1);
      fromDate = from.toISOString();
    } else if (datePreset === "custom") {
      if (!customFromDate && !customToDate) return;
      if (customFromDate) fromDate = customFromDate.toDate().toISOString();
      if (customToDate) toDate = customToDate.toDate().toISOString();
    }
    // "all" → no dates

    try {
      await sendPaymentHistoryLink({
        clientId: userId,
        companyId: admin.companyId,
        deliveryChannel,
        fromDate,
        toDate,
      }).unwrap();
      setSendSuccess(t('PAYMENT_HISTORY_LINK_SENT'));
    } catch (error: any) {
      const errorKey = error?.data?.error;
      if (errorKey === "client_has_no_phone") {
        setSendError(t('CLIENT_HAS_NO_PHONE'));
      } else if (errorKey === "client_has_no_email") {
        setSendError(t('CLIENT_HAS_NO_EMAIL'));
      } else {
        setSendError(t('ERROR_SENDING_PAYMENT_HISTORY'));
      }
    }
  };

  const handleSendInvoiceBundle = async () => {
    if (!sendViaSms && !sendViaEmail) return;

    setSendError("");
    setSendSuccess("");

    const deliveryChannel = sendViaSms && sendViaEmail ? 3 : sendViaSms ? 1 : 2;

    try {
      await sendInvoiceBundleLink({
        clientId: userId,
        companyId: admin.companyId,
        deliveryChannel,
      }).unwrap();
      setSendSuccess(t('INVOICE_BUNDLE_LINK_SENT'));
    } catch (error: any) {
      const errorKey = error?.data?.error;
      if (errorKey === "client_has_no_phone") {
        setSendError(t('CLIENT_HAS_NO_PHONE'));
      } else if (errorKey === "client_has_no_email") {
        setSendError(t('CLIENT_HAS_NO_EMAIL'));
      } else if (errorKey === "no_invoices_found") {
        setSendError(t('NO_INVOICES_TO_SEND'));
      } else {
        setSendError(t('ERROR_SENDING_INVOICE_BUNDLE'));
      }
    }
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Loader />
      </div>
    );
  }

  // Calculate total number of transactions (using flattened transactions array)
  const totalTransactions = allTransactions.length;

  // Calculate total sum of all transactions (refunds are subtracted)
  const totalSum = allTransactions.reduce((sum, transaction) => {
    const amount = transaction.amount || 0;
    // If this is a refund transaction, subtract it
    if (transaction.transactionUidZikuy) {
      return sum - Math.abs(amount);
    }
    return sum + amount;
  }, 0);

  return (
    <div className={styles.paymentTableContainer}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: '12px' }}>
        {!hideTitle && <h2 className={styles.tableTitle}>{t('USER_PAYMENTS_HISTORY')}</h2>}
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div className={styles.totalTransactions}>
            {t('TOTAL_TRANSACTIONS', { count: totalTransactions })}
          </div>
          <div className={styles.totalTransactions}>
            {t('TOTAL_SUM')}: {formatAmount(totalSum)}
          </div>
          <ExportTableButtons
            entity="UserPaymentsHistory"
            dataRows={exportRows}
            disabled={!allTransactions?.length}
          />
        </div>
      </div>

      {/* Send Payment History — separated row with clear visual grouping */}
      {(
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          marginBottom: 16,
          padding: '10px 16px',
          background: '#f8fafc',
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
          width: 'fit-content',
          direction: isRtl ? 'rtl' : 'ltr',
          flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>
            {t('SEND_PAYMENT_HISTORY')}:
          </span>

          {/* Delivery channel checkboxes */}
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <label style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              fontSize: '13px', color: clientPhone ? '#334155' : '#94a3b8',
              cursor: clientPhone ? 'pointer' : 'not-allowed',
            }}>
              <input
                type="checkbox"
                checked={sendViaSms}
                onChange={(e) => setSendViaSms(e.target.checked)}
                disabled={!clientPhone || isSendingLink || isSendingInvoices}
                style={{ accentColor: '#2c3e6b' }}
              />
              SMS
            </label>
            <label style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              fontSize: '13px', color: clientEmail ? '#334155' : '#94a3b8',
              cursor: clientEmail ? 'pointer' : 'not-allowed',
            }}>
              <input
                type="checkbox"
                checked={sendViaEmail}
                onChange={(e) => setSendViaEmail(e.target.checked)}
                disabled={!clientEmail || isSendingLink || isSendingInvoices}
                style={{ accentColor: '#2c3e6b' }}
              />
              {t('EMAIL')}
            </label>
            <span
              title={t('DELIVERY_CHANNEL_INFO')}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: '16px', height: '16px', borderRadius: '50%',
                backgroundColor: '#e2e8f0', color: '#475569',
                fontSize: '11px', fontWeight: 700, cursor: 'help',
                lineHeight: 1, flexShrink: 0,
              }}
            >i</span>
          </div>

          {/* Separator */}
          <div style={{ width: '1px', height: '24px', background: '#cbd5e1' }} />

          {/* Date range preset selector */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', fontWeight: 500, color: '#475569', whiteSpace: 'nowrap' }}>
              {t('DATE_RANGE')}:
            </span>
            <select
              value={datePreset}
              onChange={(e) => setDatePreset(e.target.value as DatePreset)}
              disabled={isSendingLink || isSendingInvoices}
              style={{
                fontSize: '13px',
                padding: '4px 8px',
                borderRadius: '4px',
                border: '1px solid #cbd5e1',
                background: '#fff',
                color: '#334155',
                cursor: (isSendingLink || isSendingInvoices) ? 'not-allowed' : 'pointer',
                direction: isRtl ? 'rtl' : 'ltr',
              }}
            >
              <option value="all">{t('DATE_RANGE_ALL')}</option>
              <option value="3months">{t('DATE_RANGE_3_MONTHS')}</option>
              <option value="6months">{t('DATE_RANGE_6_MONTHS')}</option>
              <option value="1year">{t('DATE_RANGE_1_YEAR')}</option>
              <option value="custom">{t('DATE_RANGE_CUSTOM')}</option>
            </select>
          </div>

          {/* Custom date pickers — shown only when "custom" is selected */}
          {datePreset === "custom" && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#64748b' }}>
                {t('FROM')}:
                <DatePicker
                  value={customFromDate}
                  onChange={(val: DateObject) => setCustomFromDate(val)}
                  format="DD/MM/YYYY"
                  disabled={isSendingLink}
                  maxDate={customToDate || new DateObject()}
                  style={{
                    fontSize: '12px',
                    padding: '3px 6px',
                    borderRadius: '4px',
                    border: '1px solid #cbd5e1',
                    width: '110px',
                    textAlign: 'center',
                  }}
                  containerStyle={{ direction: 'ltr' }}
                />
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#64748b' }}>
                {t('TO')}:
                <DatePicker
                  value={customToDate}
                  onChange={(val: DateObject) => setCustomToDate(val)}
                  format="DD/MM/YYYY"
                  disabled={isSendingLink}
                  minDate={customFromDate || undefined}
                  maxDate={new DateObject()}
                  style={{
                    fontSize: '12px',
                    padding: '3px 6px',
                    borderRadius: '4px',
                    border: '1px solid #cbd5e1',
                    width: '110px',
                    textAlign: 'center',
                  }}
                  containerStyle={{ direction: 'ltr' }}
                />
              </label>
            </div>
          )}

          <button
            onClick={handleSendPaymentHistory}
            disabled={isSendingLink || isSendingInvoices || (!sendViaSms && !sendViaEmail) || (datePreset === 'custom' && !customFromDate && !customToDate)}
            style={{
              padding: '7px 18px',
              fontSize: '13px',
              fontWeight: 600,
              borderRadius: '6px',
              border: 'none',
              cursor: (isSendingLink || isSendingInvoices || (!sendViaSms && !sendViaEmail) || (datePreset === 'custom' && !customFromDate && !customToDate)) ? 'not-allowed' : 'pointer',
              backgroundColor: (isSendingLink || isSendingInvoices || (!sendViaSms && !sendViaEmail) || (datePreset === 'custom' && !customFromDate && !customToDate)) ? '#cbd5e1' : '#2c3e6b',
              color: '#fff',
              whiteSpace: 'nowrap',
              transition: 'background-color 0.15s ease',
            }}
          >
            {isSendingLink ? t('SENDING') + '...' : t('SEND_PAYMENT_HISTORY')}
          </button>
          <button
            onClick={handleSendInvoiceBundle}
            disabled={isSendingLink || isSendingInvoices || (!sendViaSms && !sendViaEmail)}
            style={{
              padding: '7px 18px',
              fontSize: '13px',
              fontWeight: 600,
              borderRadius: '6px',
              border: '1px solid #2c3e6b',
              cursor: (isSendingLink || isSendingInvoices || (!sendViaSms && !sendViaEmail)) ? 'not-allowed' : 'pointer',
              backgroundColor: (isSendingLink || isSendingInvoices || (!sendViaSms && !sendViaEmail)) ? '#f1f5f9' : '#fff',
              color: (isSendingLink || isSendingInvoices || (!sendViaSms && !sendViaEmail)) ? '#94a3b8' : '#2c3e6b',
              whiteSpace: 'nowrap',
              transition: 'background-color 0.15s ease',
            }}
          >
            {isSendingInvoices ? t('SENDING') + '...' : t('SEND_LAST_5_INVOICES')}
          </button>
        </div>
      )}

      {/* Fixed position notifications - won't cause layout shift */}
      {error && (
        <div className={styles.toastNotification} style={{ backgroundColor: '#fef2f2', borderColor: '#ef4444', color: '#dc2626' }}>
          {t('ERROR_LOADING_PAYMENTS')}
        </div>
      )}

      {refundError && (
        <div className={styles.toastNotification} style={{ backgroundColor: '#fef2f2', borderColor: '#ef4444', color: '#dc2626' }}>
          {refundError}
        </div>
      )}

      {refundSuccess && (
        <div className={styles.toastNotification} style={{ backgroundColor: '#f0fdf4', borderColor: '#22c55e', color: '#16a34a' }}>
          {refundSuccess}
        </div>
      )}

      {sendError && (
        <div className={styles.toastNotification} style={{ backgroundColor: '#fef2f2', borderColor: '#ef4444', color: '#dc2626' }}>
          {sendError}
        </div>
      )}

      {sendSuccess && (
        <div className={styles.toastNotification} style={{ backgroundColor: '#f0fdf4', borderColor: '#22c55e', color: '#16a34a' }}>
          {sendSuccess}
        </div>
      )}

      {!allTransactions || allTransactions.length === 0 ? (
        <div className={styles.noData}>
          {t('NO_USER_PAYMENTS_FOUND')}
        </div>
      ) : (
        <div className={styles.tableContainer}>
          {/* Bulk Actions Bar - disabled for now */}
          {/* <div className={`${styles.bulkActionsBar} ${selectedTransactionIds.size > 0 ? styles.bulkActionsBarVisible : ''}`}>
            <span className={styles.selectedCount}>
              {t('SELECTED_TRANSACTIONS', { count: selectedTransactionIds.size })}
            </span>
            <button
              className={styles.bulkRefundButton}
              onClick={() => setShowBulkRefundModal(true)}
              disabled={isBulkRefunding}
            >
              {t('BULK_REFUND')} ({selectedTransactionIds.size})
            </button>
            <button
              className={styles.clearSelectionButton}
              onClick={() => setSelectedTransactionIds(new Set())}
            >
              {t('CLEAR_SELECTION')}
            </button>
          </div> */}

          <div className={styles.tableWrapper}>
          <table className={styles.paymentTable}>
            <thead>
              <tr className={styles.headerRow}>
                <th className={styles.headerCell}>{t('PAYMENT_TYPE')}</th>
                <th className={styles.headerCell}>{t('DOCUMENT_TYPE')}</th>
                <th className={styles.headerCell}>{t('PAYMENT_AMOUNT')}</th>
                <th className={styles.headerCell}>{t('PAYMENT_DATE')}</th>
                <th className={styles.headerCell}>{t('INVOICE_NUMBER')}</th>
                <th className={styles.headerCell}>{t('DETAILS')}</th>
                <th className={styles.headerCell}>{t('COMMENTS')}</th>
                <th className={styles.headerCell}>{t('ACTIONS')}</th>
              </tr>
            </thead>
            <tbody>
              {hierarchicalTransactions.map((transaction, index) => {
                const txIsManualAdj = !!(transaction as HierarchicalTransaction).isManualAdjustment;
                const txIsRefund = !txIsManualAdj && transaction.transactionUidZikuy ? true : false;
                const txHasBeenRefunded = !txIsManualAdj && hasBeenRefunded(transaction.transactionUid);
                const remainingAmount = !txIsManualAdj ? getRemainingRefundAmount(transaction.transactionUid, transaction.amount) : 0;
                // Allow refund if: not a refund transaction, has transaction UID, and has remaining amount to refund
                const canRefund = !txIsManualAdj && !txIsRefund && transaction.transactionUid && remainingAmount > 0;
                const originalTx = txIsRefund ? getOriginalTransactionForRefund(transaction.transactionUidZikuy!) : null;
                const refunds = txHasBeenRefunded ? getRefundsForTransaction(transaction.transactionUid) : [];
                const isChildRefund = transaction.isChildRefund;
                
                const isRapidRecover =
                  !txIsManualAdj && !!transaction.isRapidInvoiceRecover;

                return (
                <tr key={transaction.id || index} className={`${styles.dataRow} ${txIsRefund ? styles.refundRow : ''} ${isChildRefund ? styles.childRefundRow : ''} ${isRapidRecover ? styles.rapidInvoiceRecoverRow : ''}`}
                  style={txIsManualAdj ? { backgroundColor: '#fffbeb' } : undefined}
                >
                  <td className={styles.dataCell}>
                    {txIsManualAdj
                      ? <span style={{ fontStyle: 'italic', color: '#b45309' }}>✏️ {t('MANUAL_BALANCE_ADJUSTMENT')}</span>
                      : (transaction.paymentName || t('CREDIT_CARD'))}
                  </td>
                  <td className={styles.dataCell}>
                    {txIsManualAdj ? '-' : (transaction.paymentDocumentTypeName || '-')}
                  </td>
                  <td className={styles.dataCell} style={
                    txIsManualAdj ? { fontWeight: 700, color: transaction.amount >= 0 ? '#16a34a' : '#dc2626', direction: 'ltr', unicodeBidi: 'embed' as const } :
                    txIsRefund ? { color: '#dc2626', fontWeight: 700, direction: 'ltr', unicodeBidi: 'embed' as const } :
                    { fontWeight: 700 }
                  }>
                    {txIsManualAdj
                      ? `${transaction.amount >= 0 ? '+' : ''}${formatAmount(transaction.amount)}`
                      : txIsRefund ? `-${formatAmount(Math.abs(transaction.amount))}` : formatAmount(transaction.amount)}
                  </td>
                  <td className={styles.dataCell}>{formatDate(transaction.timestamp)}</td>
                  <td className={styles.dataCell}>
                    {txIsManualAdj ? '-' : (
                      <span className={styles.invoiceNumberCell}>
                        {transaction.invoiceLink ? (
                          <a
                            href={transaction.invoiceLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.invoiceLink}
                          >
                            {transaction.invoiceNumber || '-'}
                          </a>
                        ) : (
                          transaction.invoiceNumber || '-'
                        )}
                        {rapidInvoiceRecoverMark(!!transaction.isRapidInvoiceRecover)}
                      </span>
                    )}
                  </td>
                  <td className={`${styles.dataCell} ${styles.commentCell}`}>
                    {txIsManualAdj ? (() => {
                      const adj = transaction as HierarchicalTransaction;
                      const balanceStr = `${formatAmount(adj.prevBalance ?? 0)} → ${formatAmount(adj.newBalance ?? 0)}`;
                      const adminStr = adj.adminName ? ` (${adj.adminName})` : '';
                      const detailsText = `${t('BALANCE_CHANGE')}: ⁦${balanceStr}⁩${adminStr}`;
                      return (
                        <div className={styles.commentContainer}>
                          <span
                            className={styles.commentText}
                            onClick={() => setExpandedText({ titleKey: 'DETAILS', content: detailsText })}
                            title={detailsText}
                          >
                            {t('BALANCE_CHANGE')}: <span dir="ltr" style={{ unicodeBidi: 'isolate' }}>{balanceStr}</span>{adminStr}
                          </span>
                          <div className={styles.commentTooltip}>
                            {t('BALANCE_CHANGE')}: <span dir="ltr" style={{ unicodeBidi: 'isolate' }}>{balanceStr}</span>{adminStr}
                          </div>
                        </div>
                      );
                    })() : txIsRefund ? (
                      <span className={styles.refundLabel}>↩️ #{transaction.parentInvoice || originalTx?.invoiceNumber || '-'}</span>
                    ) : txHasBeenRefunded ? (
                      <span className={styles.refundedLabel}>
                        {t('TOTAL_PAID')}: {formatAmount(remainingAmount)}
                      </span>
                    ) : (
                      <span style={{ color: '#9ca3af' }}>—</span>
                    )}
                  </td>
                  <td className={`${styles.dataCell} ${styles.commentCell}`}>
                    {transaction.comments ? (
                      <div className={styles.commentContainer}>
                        <span
                          className={styles.commentText}
                          onClick={() => setExpandedText({ titleKey: 'COMMENTS', content: transaction.comments || '' })}
                          title={transaction.comments}
                        >
                          {transaction.comments}
                        </span>
                        <div className={styles.commentTooltip}>
                          {transaction.comments}
                        </div>
                      </div>
                    ) : (
                      <span className={styles.commentEmpty}>—</span>
                    )}
                  </td>
                  <td className={styles.dataCell}>
                    {txIsManualAdj ? (
                      <span style={{ color: '#9ca3af' }}>—</span>
                    ) : (
                    <div className={styles.transactionActions}>
                      {/* Checkbox disabled - bulk refund not available */}
                      {/* <input
                        type="checkbox"
                        checked={isTransactionSelected(transaction.id)}
                        onChange={(e) => handleTransactionSelection(transaction.id, e.target.checked)}
                        className={styles.transactionCheckbox}
                        title={t('SELECT_TRANSACTION')}
                      /> */}
                      {refundingTransactionIds.has(transaction.id) ? (
                        <div className={styles.refundLoader}>
                          <Loader />
                        </div>
                      ) : (
                        <button
                          className={styles.refundButton}
                          onClick={() => openRefundModal(transaction.id, transaction.transactionUid, transaction.amount, transaction.paymentsTypesId || 1)}
                          disabled={refundingTransactionIds.has(transaction.id) || !canRefund}
                          title={
                            !transaction.transactionUid ? t('NO_TRANSACTION_UID') :
                            txIsRefund ? t('CANNOT_REFUND_REFUND') :
                            remainingAmount <= 0 ? t('FULLY_REFUNDED') :
                            undefined
                          }
                        >
                          {t('REFUND')}
                        </button>
                      )}
                    </div>
                    )}
                  </td>
                </tr>
              )})}
            </tbody>
          </table>
        </div>

        {/* Single Refund Modal */}
        {refundModal.isOpen && (
          <div className={styles.modalOverlay}>
            <div className={styles.modal}>
              <h3 className={styles.modalTitle}>{t('REFUND_TRANSACTION')}</h3>
              <p className={styles.modalText}>
                {t('ORIGINAL_AMOUNT')}: {formatAmount(refundModal.transactionAmount)}
              </p>
              {refundModal.maxRefundAmount < refundModal.transactionAmount && (
                <p className={styles.modalTextWarning}>
                  {t('ALREADY_REFUNDED')}: {formatAmount(refundModal.transactionAmount - refundModal.maxRefundAmount)}
                </p>
              )}
              <p className={styles.modalText}>
                {t('MAX_REFUND_AMOUNT')}: {formatAmount(refundModal.maxRefundAmount)}
              </p>
              <div className={styles.formGroup}>
                <label htmlFor="refundAmount" className={styles.label}>
                  {t('REFUND_AMOUNT')} *
                </label>
                <input
                  type="number"
                  id="refundAmount"
                  value={refundModal.refundAmount}
                  onChange={(e) => setRefundModal(prev => ({ ...prev, refundAmount: e.target.value }))}
                  className={styles.textInput}
                  placeholder={t('ENTER_REFUND_AMOUNT')}
                  max={refundModal.maxRefundAmount}
                  min={0}
                  step="0.01"
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="singleRefundReason" className={styles.label}>
                  {t('REFUND_REASON')} *
                </label>
                <textarea
                  id="singleRefundReason"
                  value={refundModal.refundReason}
                  onChange={(e) => setRefundModal(prev => ({ ...prev, refundReason: e.target.value }))}
                  className={styles.textArea}
                  placeholder={t('ENTER_REFUND_REASON')}
                  rows={3}
                />
              </div>
              {refundError && (
                <div className={styles.errorMessage}>
                  {refundError}
                </div>
              )}
              <div className={styles.modalActions}>
                <button
                  className={styles.cancelButton}
                  onClick={closeRefundModal}
                  disabled={refundingTransactionIds.has(refundModal.transactionId || 0)}
                >
                  {t('CANCEL')}
                </button>
                <button
                  className={styles.confirmRefundButton}
                  onClick={handleRefundSubmit}
                  disabled={refundingTransactionIds.has(refundModal.transactionId || 0) || !refundModal.refundReason.trim() || !refundModal.refundAmount}
                >
                  {refundingTransactionIds.has(refundModal.transactionId || 0) ? (
                    <>
                      <Loader /> {t('PROCESSING')}...
                    </>
                  ) : (
                    t('REFUND')
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Refund Modal */}
        {showBulkRefundModal && (
          <div className={styles.modalOverlay}>
            <div className={styles.modal}>
              <h3 className={styles.modalTitle}>{t('BULK_REFUND_CONFIRMATION')}</h3>
              <p className={styles.modalText}>
                {t('BULK_REFUND_MESSAGE', { count: selectedTransactionIds.size })}
              </p>
              <div className={styles.formGroup}>
                <label htmlFor="refundReason" className={styles.label}>
                  {t('REFUND_REASON')} *
                </label>
                <textarea
                  id="refundReason"
                  value={bulkRefundReason}
                  onChange={(e) => setBulkRefundReason(e.target.value)}
                  className={styles.textArea}
                  placeholder={t('ENTER_REFUND_REASON')}
                  rows={3}
                />
              </div>
              <div className={styles.modalActions}>
                <button
                  className={styles.cancelButton}
                  onClick={() => {
                    setShowBulkRefundModal(false);
                    setBulkRefundReason("");
                  }}
                  disabled={isBulkRefunding}
                >
                  {t('CANCEL')}
                </button>
                <button
                  className={styles.confirmRefundButton}
                  onClick={handleBulkRefund}
                  disabled={isBulkRefunding || !bulkRefundReason.trim()}
                >
                  {isBulkRefunding ? (
                    <>
                      <Loader /> {t('PROCESSING')}...
                    </>
                  ) : (
                    `${t('REFUND')} ${selectedTransactionIds.size} ${t('TRANSACTIONS')}`
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Expanded Text Modal (generic — used for comments and details) */}
        {expandedText && (
          <>
            <div className={styles.commentModalOverlay} onClick={() => setExpandedText(null)} />
            <div className={styles.commentModal}>
              <button className={styles.commentModalClose} onClick={() => setExpandedText(null)}>
                ×
              </button>
              <h4 className={styles.commentModalTitle}>{t(expandedText.titleKey)}</h4>
              <div className={styles.commentModalContent}>
                {expandedText.content}
              </div>
            </div>
          </>
        )}
        </div>
      )}
    </div>
  );
};

export default UserPaymentTable;
